import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { errMsg } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;

export default function IdentityScore() {
    const [form, setForm] = useState({
        full_name: "", specialization: "", city: "",
        mobile: "", email: "", existing_website: "",
        social_links: "", years_experience: "", google_business_url: "",
    });
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    async function submit(e) {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                ...form,
                years_experience: form.years_experience ? Number(form.years_experience) : null,
                social_links: form.social_links
                    ? form.social_links.split(/[\n,]/).map((s) => s.trim()).filter(Boolean)
                    : [],
            };
            const r = await axios.post(`${API}/identity-score`, payload);
            setResult(r.data.result);
        } catch (err) {
            toast.error(errMsg(err, "Score check failed"));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-screen bg-[#F5F8FA] p-6" data-testid="identity-score-page">
            <div className="max-w-3xl mx-auto">
                <div className="mb-6">
                    <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">Free Tool</div>
                    <h1 className="text-3xl md:text-4xl font-bold text-[#123B5D] flex items-center gap-2">
                        <ShieldCheck size={28} /> Check your FREE Digital Identity Score
                    </h1>
                    <p className="text-sm text-[#17212B]/60 mt-2">
                        Answer the fields below — we score ONLY what you provide (no scraping, no fake scans).
                        Get a completeness grade + 3 concrete actions to improve.
                    </p>
                </div>

                {!result ? (
                    <form onSubmit={submit} className="bg-white rounded-xl p-6 shadow-sm space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div><Label>Full name *</Label><Input required value={form.full_name} onChange={upd("full_name")} data-testid="score-name" /></div>
                            <div><Label>Specialization *</Label><Input required value={form.specialization} onChange={upd("specialization")} data-testid="score-spec" /></div>
                            <div><Label>City *</Label><Input required value={form.city} onChange={upd("city")} data-testid="score-city" /></div>
                            <div><Label>Years of experience</Label><Input type="number" value={form.years_experience} onChange={upd("years_experience")} data-testid="score-years" /></div>
                            <div><Label>Mobile</Label><Input value={form.mobile} onChange={upd("mobile")} data-testid="score-mobile" /></div>
                            <div><Label>Email</Label><Input type="email" value={form.email} onChange={upd("email")} data-testid="score-email" /></div>
                            <div className="md:col-span-2"><Label>Existing website URL</Label><Input type="url" placeholder="https://…" value={form.existing_website} onChange={upd("existing_website")} data-testid="score-website" /></div>
                            <div className="md:col-span-2"><Label>Social profile URLs (one per line or comma-separated)</Label>
                                <textarea rows={3} className="w-full border rounded-md p-2 text-sm" value={form.social_links} onChange={upd("social_links")} placeholder="https://linkedin.com/in/…&#10;https://instagram.com/…" data-testid="score-social" />
                            </div>
                            <div className="md:col-span-2"><Label>Google Business Profile URL</Label><Input type="url" value={form.google_business_url} onChange={upd("google_business_url")} data-testid="score-gbp" /></div>
                        </div>
                        <Button type="submit" disabled={loading} data-testid="score-submit">
                            <Sparkles size={13} /> {loading ? "Scoring…" : "Get my score"}
                        </Button>
                    </form>
                ) : (
                    <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="score-result">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">Your Digital Identity Score</div>
                                <div className="text-5xl font-bold text-[#123B5D]" data-testid="score-value">{result.score}<span className="text-lg text-[#17212B]/50">/{result.max}</span></div>
                            </div>
                            <div className={`text-3xl font-bold px-4 py-2 rounded-lg ${
                                result.grade === "A" ? "bg-[#15803D]/10 text-[#15803D]" :
                                result.grade === "B" ? "bg-[#0F766E]/10 text-[#0F766E]" :
                                result.grade === "C" ? "bg-[#D97706]/10 text-[#D97706]" :
                                "bg-[#DC2626]/10 text-[#DC2626]"
                            }`} data-testid="score-grade">Grade {result.grade}</div>
                        </div>
                        <div className="space-y-2 mb-4">
                            {result.dimensions.map((d) => (
                                <div key={d.name} className="flex items-center justify-between border-b py-2 text-sm">
                                    <div><b>{d.name}</b><div className="text-xs text-[#17212B]/60">{d.note}</div></div>
                                    <div className="font-mono text-[#123B5D]">{d.score}/{d.max}</div>
                                </div>
                            ))}
                        </div>
                        <div className="bg-[#F5F8FA] rounded-lg p-4 mb-4">
                            <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold mb-2">Top 3 actions</div>
                            <ul className="list-disc pl-5 text-sm text-[#17212B]/80 space-y-1">
                                {result.top_actions.map((a, i) => <li key={i}>{a}</li>)}
                            </ul>
                        </div>
                        <p className="text-xs text-[#17212B]/50 mb-4">{result.disclaimer}</p>
                        <Link to="/signup" className="inline-flex items-center gap-2 bg-[#0F766E] text-white px-5 py-3 rounded-full font-semibold hover:bg-[#0d665e]" data-testid="score-cta-signup">
                            Improve your score — Create your Digital Identity <ArrowRight size={16} />
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}
